/**
 * NOTE: unified/remark/rehype are ESM-only packages and break Jest
 * configs that don't transform them. The shared loader in
 * `./unified-loader` defers their import to call time.
 */
import { loadRemarkProcessor } from './unified-loader';
import { aiMimeTypes, citationLinkText } from '../../constants';

interface MdastNodeLike {
  type: string;
  children?: MdastNodeLike[];
  lang?: string | null;
  value?: string;
}

/**
 * Converts a markdown string to an HTML string. Lazily imports the
 * unified/remark/rehype ESM modules on first call.
 */
export async function convertMarkdownToHtml(markdown: string): Promise<string> {
  const { createProcessor } = await loadRemarkProcessor();
  const [rehypeRawMod, rehypeSanitizeMod, rehypeStringifyMod, remarkRehypeMod] = await Promise.all([
    import('rehype-raw'),
    import('rehype-sanitize'),
    import('rehype-stringify'),
    import('remark-rehype'),
  ]);
  const rehypeRaw = rehypeRawMod.default;
  const rehypeSanitize = rehypeSanitizeMod.default;
  const rehypeStringify = rehypeStringifyMod.default;
  const remarkRehype = remarkRehypeMod.default;

  /**
   * Remark plugin to normalize Markdown AST prior to converting to HTML.
   *
   * - Remove code blocks (triple backticks) from markdown.
   * - Except for code blocks with `lang="markdown-draft-document"` which
   *   we parse as Markdown (Markdown in Markdown).
   * - Strip citation anchors so "Citation" doesn't linger in the output.
   */
  const remarkNormalizeForConvertToHtml = function () {
    const markdownProcessor = createProcessor();
    const getTextContent = (node: MdastNodeLike): string => {
      if (node.type === 'text') return node.value ?? '';
      if (Array.isArray(node.children)) {
        return node.children.map(getTextContent).join('');
      }
      return '';
    };
    return (tree: MdastNodeLike) => {
      const visit = (
        node: MdastNodeLike,
        index: number | undefined,
        parent: MdastNodeLike | undefined
      ): number | undefined => {
        if (node.type === 'code' && parent && index !== undefined && parent.children) {
          if (node.lang === aiMimeTypes.markdownDraftDocument) {
            const parsedTree = markdownProcessor.parse(node.value ?? '');
            parent.children.splice(index, 1, ...parsedTree.children);
            return index;
          }
          if (node.lang !== aiMimeTypes.plainText) {
            parent.children.splice(index, 1);
            return index;
          }
        }
        if (node.type === 'link' && parent && index !== undefined && parent.children) {
          if (getTextContent(node) === citationLinkText) {
            parent.children.splice(index, 1);
            return index;
          }
        }
        if (Array.isArray(node.children)) {
          let i = 0;
          while (i < node.children.length) {
            const result = visit(node.children[i], i, node);
            i = result !== undefined ? result : i + 1;
          }
        }
        return undefined;
      };
      visit(tree, undefined, undefined);
      return tree;
    };
  };

  const result = await createProcessor()
    .use(remarkNormalizeForConvertToHtml)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeSanitize)
    .use(rehypeStringify)
    .process(markdown);

  return String(result);
}
